import { AloftArea, AloftPrice } from '@aloft/toolkit-shared';

import { useCallback } from 'react';

import { setOpinionOfSiteValue } from '../../../redux/features/actions/setOpinionOfSiteValue.ts';
import { sliderAdjustment } from '../../../redux/features/actions/sliderAdjustment.ts';
import { selectCostNewByCategories } from '../../../redux/features/selectors/costSelectors/selectCostNewByCategory.ts';
import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectDepreciationSettings,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectImprovementsEntities } from '../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectSiteEntities } from '../../../redux/features/selectors/propertyDataSelectors/siteSelectors.ts';
import { selectTransactionEntities } from '../../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { selectImprovementsCosts } from '../../../redux/features/selectors/selectImprovementsCost.ts';
import { selectSiteValues } from '../../../redux/features/selectors/selectSiteValue.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { computeImprovementValuePerAgeYearForProperty } from './economicLife.ts';
import {
  type PropertyData,
  alignSiteValuesToCurve,
  buildCurve,
  getExpectedPerSF,
  getViewLocationRating,
} from './siteValueCurve.ts';

export function useSiteValueAdjuster() {
  const dispatch = useAppDispatch();
  const compSequence = useAppSelector(selectCompSequence);
  const adjustmentSettings = useAppSelector(selectAdjustmentSettings);
  const siteEntities = useAppSelector(selectSiteEntities);
  const improvementsEntities = useAppSelector(selectImprovementsEntities);
  const depreciationSettings = useAppSelector(selectDepreciationSettings);
  const improvementsCosts = useAppSelector(selectImprovementsCosts);
  const costNewByCategories = useAppSelector(selectCostNewByCategories);
  const siteValues = useAppSelector(selectSiteValues);
  const transactionEntities = useAppSelector(selectTransactionEntities);
  const depreciateSeparately = useAppSelector(
    (state) => state.order.depreciationSettings.depreciateSeparately || false,
  );

  const adjust = useCallback(() => {
    const properties: PropertyData[] = [];

    // Subject
    const subjectSite = siteEntities.subject;
    const subjectImprovements = improvementsEntities.subject;
    const subjectSiteValue = siteValues.subject;
    const subjectTransaction = transactionEntities.subject;

    if (subjectSite && subjectImprovements && subjectSiteValue && subjectTransaction) {
      const lotSize = subjectSite.area
        ? AloftArea.schema.parse(subjectSite.area).getUnitAmount('sqft')
        : 0;
      const effectiveAge = subjectImprovements.effectiveAge || 0;
      const economicLife =
        // Prefer a property-specific totalEconomicLife if provided
        ('totalEconomicLife' in subjectImprovements &&
        typeof subjectImprovements.totalEconomicLife === 'number'
          ? subjectImprovements.totalEconomicLife
          : undefined) ??
        (subjectImprovements.propertyType === 'manufacturedHome'
          ? (depreciationSettings.defaultEcoLifeManufactured ?? depreciationSettings.defaultEcoLife)
          : depreciationSettings.defaultEcoLife) ??
        60;
      const viewLocationRating = getViewLocationRating(subjectSite.view);
      const siteValue = subjectSiteValue.toDollarLike();
      const salePrice = subjectTransaction.price
        ? AloftPrice.schema.parse(subjectTransaction.price).toDollarLike()
        : 0;
      const improvementValue = improvementsCosts.subject?.toDollarLike() ?? 0;
      const improvementValuePerAgeYear = computeImprovementValuePerAgeYearForProperty({
        depreciateSeparately,
        improvementValue,
        effectiveAge,
        defaultEconomicLife: economicLife,
        byCategory: costNewByCategories.subject,
        improvementsEco: subjectImprovements,
        depreciationDefaults: depreciationSettings,
      });

      properties.push({
        id: 'subject',
        lotSize,
        effectiveAge,
        economicLife,
        viewLocationRating,
        siteValue,
        salePrice,
        improvementValue,
        improvementValuePerAgeYear,
      });
    }

    // Comps
    compSequence.forEach((compId) => {
      const compSite = siteEntities[compId];
      const compImprovements = improvementsEntities[compId];
      const compSiteValue = siteValues[compId];
      const compTransaction = transactionEntities[compId];

      if (compSite && compImprovements && compSiteValue && compTransaction) {
        const lotSize = compSite.area
          ? AloftArea.schema.parse(compSite.area).getUnitAmount('sqft')
          : 0;
        const effectiveAge = compImprovements.effectiveAge || 0;
        const economicLife =
          // Prefer a property-specific totalEconomicLife if provided
          ('totalEconomicLife' in compImprovements &&
          typeof compImprovements.totalEconomicLife === 'number'
            ? compImprovements.totalEconomicLife
            : undefined) ??
          (compImprovements.propertyType === 'manufacturedHome'
            ? (depreciationSettings.defaultEcoLifeManufactured ??
              depreciationSettings.defaultEcoLife)
            : depreciationSettings.defaultEcoLife) ??
          60;
        const viewLocationRating = getViewLocationRating(compSite.view);
        const siteValue = compSiteValue.toDollarLike();
        const improvementValue = improvementsCosts[compId]?.toDollarLike() ?? 0;
        const improvementValuePerAgeYear = computeImprovementValuePerAgeYearForProperty({
          depreciateSeparately,
          improvementValue,
          effectiveAge,
          defaultEconomicLife: economicLife,
          byCategory: costNewByCategories[compId],
          improvementsEco: compImprovements,
          depreciationDefaults: depreciationSettings,
        });
        const salePrice = compTransaction.price
          ? AloftPrice.schema.parse(compTransaction.price).toDollarLike()
          : 0;

        properties.push({
          id: compId,
          lotSize,
          effectiveAge,
          economicLife,
          viewLocationRating,
          siteValue,
          improvementValue,
          improvementValuePerAgeYear,
          salePrice,
        });
      }
    });

    const comps = properties.filter((p) => p.id !== 'subject');
    if (comps.length === 0) return;

    const curve = buildCurve(comps);

    // Update subject site value using curve (full value, with optional rounding)
    const subject = properties.find((p) => p.id === 'subject');
    if (subject) {
      const perSFUnrounded = getExpectedPerSF(subject.lotSize, subject.viewLocationRating, curve);
      const roundingEnabled = Boolean(adjustmentSettings.enableRounding);
      const perTypeRounding =
        adjustmentSettings.rounding?.site ?? adjustmentSettings.roundingDefault;
      const roundingStep = AloftPrice.schema.parse(perTypeRounding).toDollarLike();
      const totalUnrounded = perSFUnrounded * subject.lotSize;
      const totalRounded =
        roundingEnabled && roundingStep > 0
          ? Math.round(totalUnrounded / roundingStep) * roundingStep
          : totalUnrounded;

      dispatch(setOpinionOfSiteValue(AloftPrice.fromDollarLike(totalRounded).toJSON()));
    }

    // Align comparable site values to the curve via effective age adjustments
    const sortedComps = comps.sort((a, b) => {
      const aIndex = compSequence.indexOf(a.id);
      const bIndex = compSequence.indexOf(b.id);
      return aIndex - bIndex;
    });
    const adjustedAges = alignSiteValuesToCurve(sortedComps, curve);

    // Aggregate size of changes to skip if trivial (clamp to actual age before comparing)
    let totalAdjustmentNeeded = 0;
    Object.entries(adjustedAges).forEach(([compId, newAge]) => {
      if (compId !== 'subject') {
        const currentAge = improvementsEntities[compId]?.effectiveAge || 0;
        const actualAge = improvementsEntities[compId]?.age;
        const roundedAge = Math.round(newAge);
        const clampedAge =
          typeof actualAge === 'number' ? Math.min(roundedAge, actualAge) : roundedAge;
        const ageDifference = Math.abs(clampedAge - currentAge);

        totalAdjustmentNeeded += ageDifference;
      }
    });
    if (totalAdjustmentNeeded < 1) return;

    Object.entries(adjustedAges).forEach(([compId, newAge]) => {
      if (compId !== 'subject') {
        const currentAge = improvementsEntities[compId]?.effectiveAge || 0;
        const actualAge = improvementsEntities[compId]?.age;
        const roundedAge = Math.round(newAge);
        const clampedAge =
          typeof actualAge === 'number' ? Math.min(roundedAge, actualAge) : roundedAge;
        const ageDifference = Math.abs(clampedAge - currentAge);

        if (ageDifference >= 0.5) {
          dispatch(
            sliderAdjustment({
              id: compId,
              field: 'effectiveAge',
              newValue: Math.round(clampedAge),
            }),
          );
        }
      }
    });
  }, [
    adjustmentSettings,
    compSequence,
    costNewByCategories,
    depreciateSeparately,
    dispatch,
    improvementsCosts,
    improvementsEntities,
    depreciationSettings,
    siteEntities,
    siteValues,
    transactionEntities,
  ]);

  return { adjust };
}
